import React, { type FC } from 'react'

import Icon from '@components/Icon'
import SocialMediaBar, {
  type SocialMediaLinks,
} from '@controllers/parts/SocialMediaBar'
import { trans, FOOTER_NAVIGATION } from '@helpers/translator'
import LogoVisitMonaco from '@images/logo_visitmonaco.svg?react'

interface FooterItem {
  id?: string | number
  url: string
  label: string
  isMain?: boolean
  subItems?: SubItem[]
}

interface SubItem {
  id?: string | number
  url: string
  label: string
  isMain?: boolean
}

interface FooterProps {
  socialLinks?: SocialMediaLinks
  footerItems?: FooterItem[]
  postFooterItems?: FooterItem[]
}

const renderFooterItems = (items: FooterItem[]) => (
  <ul className="flex w-full flex-col flex-wrap xl:flex-nowrap gap-y-3 md:flex-row md:gap-x-[113px] md:gap-y-4 list-none">
    {items.map((item, index) => (
      <li
        key={`footer-item-${item.id || index}`}
        className="flex flex-col space-y-3 md:max-w-[165px]"
      >
        <a href={item.url} className={'font-bold'}>
          {item.label}
        </a>
        {item.subItems && renderSubItems(item.subItems)}
      </li>
    ))}
  </ul>
)

const renderSubItems = (subItems: SubItem[]) => (
  <ul
    className={`${subItems.some((item) => item.isMain) ? 'flex' : 'hidden md:flex'} flex-col gap-y-3 list-none`}
  >
    {subItems.map((subItem, subIndex) => (
      <li
        className={subItem.isMain ? '' : 'hidden md:block'}
        key={`sub-item-${subItem.id || subIndex}`}
      >
        <a
          href={subItem.url}
          className={`${
            subItem.isMain
              ? 'font-bold'
              : 'font-semimedium md:text-sm hover-bold-no-resize hover:font-bold transition-all duration-100'
          }`}
          data-text={subItem.label}
        >
          {subItem.label}
        </a>
      </li>
    ))}
  </ul>
)

const Footer: FC<FooterProps> = ({
  socialLinks = {},
  footerItems = [],
  postFooterItems = [],
}) => {
  return (
    <div className="ltr:pl-sidebar-mobile ltr:md:pl-sidebar-desktop flex flex-col rtl:pr-sidebar-mobile rtl:md:pr-sidebar-desktop">
      <div className="flex flex-col gap-y-6 px-[85px] py-[64px] md:flex-row md:gap-y-0 md:gap-x-[84px] md:px-spacing-xl md:py-0 md:pb-11 md:mt-[84px]">
        <LogoVisitMonaco
          className="h-[38px] self-start"
          aria-label="Visit Monaco"
        />
        {footerItems?.length > 0 && (
          <nav aria-label={trans(FOOTER_NAVIGATION)}>
            {renderFooterItems(footerItems)}
          </nav>
        )}
      </div>
      {(postFooterItems?.length > 0 || Object.keys(socialLinks).length > 0) && (
        <div className="relative flex flex-col gap-y-6 bg-black px-[85px] py-[56px] md:flex-row md:items-center md:gap-y-0 md:py-[35px] ltr:md:pl-spacing-lg ltr:md:pr-spacing-xl ltr:md:ml-spacing-md rtl:md:pr-spacing-lg rtl:md:pl-spacing-xl rtl:md:mr-spacing-md">
          <Icon
            name="monaco_flag_mini"
            size="auto"
            className="absolute -top-[10px] left-[20px] md:left-spacing-lg"
          />
          <div className="flex flex-col space-y-6 pl-spacing-ld md:flex-row md:space-y-0 md:gap-x-[82px]">
            {postFooterItems?.map((item, index) => (
              <a
                key={`post-footer-${item.id || index}`}
                href={item.url}
                className="text-white text-sm font-semimedium hover-bold-no-resize hover:font-bold transition-all duration-100"
                data-text={item.label}
              >
                {item.label}
              </a>
            ))}
          </div>
          {Object.keys(socialLinks).length > 0 && (
            <div className="flex ltr:md:ml-auto rtl:md:mr-auto">
              <SocialMediaBar
                socialMediaLinks={socialLinks}
                className="text-white md:w-full md:flex"
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Footer
